import { useImmer } from "use-immer";
import { useIngredientQuery } from "./UseIngredientQueries";
import React, { useRef, useState, useEffect, useContext } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useDebounce } from "use-debounce";
import { useDelete, useStatusSelect, usePermission } from "../../../shared";
import qs from "qs";
import { getUnitList } from "../../MasterData/api/UnitApi";
import { AppContext } from "../../../store";
import moment from "moment";

export const useIngredients = ({
  detId,
  isIngredientlist,
  isTaxgrouplist,
  isTaglist,
  isList,
}) => {
  const [update, forceUpdate] = useState(false);
  const [update2, forceUpdate2] = useState(false);
  const isDeletable = usePermission("ingredient-delete");
  const isCreateVisible = usePermission("ingredient-create");
  const isEditable = usePermission("ingredient-modify");
  const validator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => message,
      validators: {
        cost: {
          required: true,
          message:
            "Please enter a number with not more than three decimal places",
          rule: (value) => {
            return /^[-]?(\d*\.\d{1,3}|\d+)$/.test(value);
          },
        },
      },
    }),
  );

  const unitValidator = useRef(
    new SimpleReactValidator({
      validators: {
        customNumeric: {
          message:
            "The value must be a number greater than 0, and cannot be exactly 0.",
          rule: (val) => {
            return parseFloat(val) > 0;
          },
          required: true,
        },
      },
    }),
  );
  const [state, setState] = useImmer({
    data: {
      pdf: [],
    },
  });
  const moduleName = "ingredients";
  const {
    globalData,
    setTableState,
    tableData,
    clearAllFilters,
    setSorting,
    setPagination,
    setSearchState,
  } = useContext(AppContext);
  const { sorting, pagination, search } = tableData[moduleName];

  const { deleteInfo, onDelete, resetDeleteInfo } = useDelete();
  const [ingredient, setIngredient] = useImmer({
    errorData: [],
    isErrorMessage: false,
    errorMessage: [],
    errorTypes: [],
    data: {
      id: 0,
      name: "",
      tax_group_id: "",
      type: "",
      status: 1,
      selectedStatus: -1,
      tags: [],
      buying_unit_id: "",
      cost_exc_tax: "",
      cost_inc_tax: "",
      tagslist: [],
      suppliers: [],
      has_uom: 0,
      use_uom: 0,
      use_uom_unit: "",
      notes: "",
      stock_tag_id: "",
      stock_category_id: "",
      stock_subcategory_id: "",
      stock_unit_id: "",
      units: [
        {
          base_unit_id: "",
          base_unit_qty: "",
          alt_unit_id: "",
          alt_unit_qty: "",
        },
      ],
      variants: [],
      sync_type: "",
      scheduled_date: "",
    },
    selectedStatus: null,
    tagslist: [],
    isLoading: true,
    isOpen: false,
    id: 0,
    del_id: 0,
    isChanged: false,
    isOpenSyncModal: false,
  });

  const [debouncedText] = useDebounce(search, 1000);
  const {
    listQuery,
    deleteIngredient,
    createIngredient,
    updateIngredient,
    ingredientDetails,
    taxListQuery,
    tagsListQuery,
    createPdfUrl,
  } = useIngredientQuery({
    detId,
    detail_id: ingredient.data.id,
    pagination,
    sorting,
    debouncedText,
    resetDeleteInfo,
    isIngredientlist,
    isTaxgrouplist,
    isTaglist,
    status: tableData.ingredients.data.selectedStatus,
    types: tableData.ingredients.data.types,
    tags: tableData.ingredients.data.tagslist,
    suppliers: tableData.ingredients.data.supplierlist,
    setIngredient,
  });

  const optionsTaxGroup =
    taxListQuery?.data?.length > 0 &&
    taxListQuery?.data?.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  const optionsTagsList = tagsListQuery?.data
    ?.filter((item) => item.type === "ingredient")
    ?.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  const optionsSupplierList = tagsListQuery?.data
    ?.filter((item) => item.type === "supplier")
    ?.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  const optionsTypes = globalData?.Ingredient_types?.map((item) => ({
    label: item.label,
    value: item.value,
  }));
  const optionsTags = tagsListQuery?.data
    ?.filter((item) => item.type === "ingredient")
    ?.map((item) => item.name);
  const optionsSupplier = tagsListQuery?.data
    ?.filter((item) => item.type === "supplier")
    ?.map((item) => item.name);
  useEffect(() => {
    const kgId = globalData?.defaultUnits.find((i) => i.label === "kg")?.value;
    if (kgId && !detId) {
      setIngredient((draft) => {
        draft.data.units[0].base_unit_id = kgId;
      });
    }
  }, [globalData]);
  const onChangeStatusHandler = (value) => {
    setTableState({
      tableName: moduleName,
      key: "selectedStatus",
      value: value,
    });
  };
  const selectStatus = useStatusSelect({ onChangeStatusHandler });
  const handleDeleteModal = (type, data) => {
    setIngredient((draft) => {
      draft.isOpen = type;
      draft.id = data;
    });
  };

  const handleClickOpen = (data, id) => {
    setIngredient((draft) => {
      draft.isOpen = data;
      draft.id = id;
    });
  };

  const handleClose = (data) => {
    setIngredient((draft) => {
      draft.isOpen = data;
      draft.id = "";
      draft.data = {
        id: 0,
        name: "",
        type: "",
        status: false,
      };
    });
    validator.current.hideMessages();
  };

  const onChange = (e) => {
    const { name, value } = e?.target;
    const tax = taxListQuery?.data?.find(
      (i) => i.id === ingredient?.data?.tax_group_id,
    )?.tax_rate;
    const taxRate = tax / 100;
    if (name === "cost_exc_tax") {
      setIngredient((draft) => {
        draft.data.cost_exc_tax = value;
        draft.data.cost_inc_tax = (
          Number(value) +
          Number(value) * taxRate
        )?.toFixed(5);
        draft.isChanged = true;
      });
    } else if (name === "cost_inc_tax") {
      setIngredient((draft) => {
        draft.data.cost_inc_tax = value;
        draft.data.cost_exc_tax = (Number(value) * (100 / (100 + tax))).toFixed(
          5,
        );
        draft.isChanged = true;
      });
    } else {
      setIngredient((draft) => {
        draft.data[name] = value;
      });
    }
    if (name === "has_uom" && value === 1 && ingredient.data.units.length < 1) {
      const kgId = globalData?.defaultUnits.find(
        (i) => i.label === "kg",
      )?.value;
      if (kgId) {
        setIngredient((draft) => {
          draft.data.units = [
            {
              base_unit_id: kgId,
              base_unit_qty: "",
              alt_unit_id: "",
              alt_unit_qty: "",
            },
          ];
          draft.isChanged = true;
        });
      }
    } else if (name === "has_uom" && value === 0) {
      setIngredient((draft) => {
        draft.data.buying_unit_id = "";
        draft.data.stock_unit_id = "";
        draft.data.use_uom = 0;
        draft.isChanged = true;
      });
    }
    if (name === "buying_unit_id") {
      setIngredient((draft) => {
        draft.isChanged = true;
      });
    }
  };
  const onChangeTax = (e) => {
    const { value } = e?.target;
    setIngredient((draft) => {
      draft.data.tax_group_id = value;
      draft.data.cost_inc_tax = "";
      draft.data.cost_exc_tax = "";
    });
  };
  const onTablehandleChange = (e, index) => {
    setIngredient((draft) => {
      draft.data.units = [
        ...draft.data.units.map((data, i) =>
          i === index ? { ...data, [e.target.name]: e.target.value } : data,
        ),
      ];
      draft.isChanged = true;
    });
  };

  const onTableChange = (e, name, index, type) => {
    setIngredient((draft) => {
      draft.data.units = [
        ...draft.data.units.map((data, i) =>
          i === index + 1
            ? { ...data, ["base_unit_id"]: e?.target?.value?.id }
            : data,
        ),
      ];
      draft.data.units = [
        ...draft.data.units.map((data, i) =>
          i === index ? { ...data, [name]: e?.target?.value } : data,
        ),
      ];
      draft.isChanged = true;
      // If the deleted unit is the one currently selected as stock unit, clear the stock unit selection
      if (
        ingredient.data.stock_unit_id ===
        ingredient.data.units[index]?.alt_unit_id
      ) {
        draft.data.stock_unit_id = "";
      }
    });
  };

  // function to handle variant change
  const onVariantChange = (e, index) => {
    const { value } = e?.target;
    setIngredient((draft) => {
      draft.data.variants = [
        ...draft.data.variants.map((data, i) =>
          i === index ? { ...data, name: value } : data,
        ),
      ];
    });
  };

  // function to add/remove variants
  const onVariantAddRemove = ({ row }) => {
    setIngredient((draft) => {
      if (typeof row == "number") {
        const result = draft.data.variants.filter((_, i) => {
          return i !== row;
        });
        draft.data.variants = result;
      } else {
        draft.data.variants.push({ id: null, name: "" });
      }
    });
  };

  useEffect(() => {
    if (ingredientDetails?.data) {
      setState((draft) => {
        draft.data.pdf = ingredientDetails?.data?.specSheets.map((i) => {
          return { url: i.doc_url, doc_name: i.doc_name };
        });
      });
      let units = [];
      ingredientDetails?.data?.units?.forEach(
        (i) =>
          !i.is_base && units.push({ ...i, alt_unit_id: i?.alt_unit_id?.id }),
      );
      setIngredient((draft) => {
        draft.isLoading = false;
        draft.data = {
          ...ingredientDetails.data,
          type: ingredientDetails?.data?.type?.toLowerCase(),
          cost_exc_tax: Number(ingredientDetails?.data?.cost_exc_tax),
          cost_inc_tax: Number(ingredientDetails?.data?.cost_inc_tax),
        };
        draft.data.units = units;
      });
    }
  }, [ingredientDetails?.data]);

  const onSubmit = () => {
    if (validator.current.allValid() && unitValidator.current.allValid()) {
      if (
        detId &&
        ingredient.isChanged &&
        ingredient.data.has_reference &&
        globalData?.pos_sync_settings?.[0]?.value
      ) {
        handleSyncModal(true);
      } else {
        handleOnSubmit();
      }
    } else {
      validator.current.showMessages();
      unitValidator.current.showMessages();
      forceUpdate(!update);
      forceUpdate2(!update2);
    }
  };

  const handleOnSubmit = (type) => {
    const payload = {
      name: ingredient.data.name,
      suppliers: ingredient.data.suppliers,
      buying_unit_id: ingredient.data.buying_unit_id,
      doc_urls: state.data.pdf.map((i) => i.url),
      cost_inc_tax: Number(ingredient.data.cost_inc_tax),
      cost_exc_tax: Number(ingredient.data.cost_exc_tax),
      type: ingredient.data.type,
      has_uom: ingredient.data.has_uom,
      use_uom: ingredient.data.use_uom,
      use_uom_unit: ingredient.data.use_uom_unit,
      status: ingredient.data.status,
      tags: ingredient.data.tags,
      tax_group_id: ingredient.data.tax_group_id,
      tax_group_name: ingredient.data.tax_group_name,
      notes: ingredient.data.notes,
      units: ingredient.data.has_uom
        ? [
            ...ingredient.data.units.map((i) => {
              return {
                alt_unit_id: i.alt_unit_id,
                base_unit_id: i.base_unit_id,
                base_unit_qty: i.base_unit_qty,
                alt_unit_qty: i.alt_unit_qty,
              };
            }),
          ]
        : [],
      variants: ingredient.data.variants,
      alternate_name: ingredient.data.alternate_name || null,
    };

    if (type === "schedule") {
      payload.sync_type = ingredient.data.sync_type;
      payload.scheduled_date = moment(ingredient.data.scheduled_date).format(
        "YYYY-MM-DD",
      );
    }

    !ingredient.data.has_uom && delete payload.units;

    if (detId) {
      updateIngredient.mutate({ detId, data: payload });
    } else {
      createIngredient.mutate({ data: payload });
    }
  };

  const searchHandler = (e) => {
    setSearchState((draft) => {
      draft.search = e.target.value;
    });
  };

  const clearSearch = () => {
    setTableState({
      tableName: moduleName,
      key: "search",
      value: "",
    });
  };

  const onSearchChange = (value) => {
    // Reset pageIndex to 1 when search term changes
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1, // Reset to the first page
      }),
    });

    // Update the search term
    setSearchState({
      tableName: moduleName,
      key: "search",
      value: value,
    });
  };

  const confirmDelete = () => {
    deleteIngredient.mutate({ id: deleteInfo.id, to: deleteInfo.to, isList });
  };
  const handleProductInputChange = (event, v) => {
    const { value, name } = event.target;
    setIngredient((draft) => {
      draft.data.tags = value;
    });
  };
  const handleSupplierInputChange = (event, v) => {
    const { value, name } = event.target;
    setIngredient((draft) => {
      draft.data.suppliers = value;
    });
  };

  const handleAddUnits = (index) => {
    if (unitValidator.current.allValid()) {
      setIngredient((draft) => {
        draft.data.units.push({
          base_unit_id: "",
          base_unit_qty: "",
          alt_unit_id: "",
          alt_unit_qty: "",
        });
        draft.isChanged = true;
      });
    } else {
      unitValidator.current.showMessages();
      forceUpdate2(!update2);
    }
  };
  const handleDelete = (type, index) => {
    setIngredient((draft) => {
      draft.data.units = [...draft.data.units.filter((data, i) => i !== index)];
      // If the deleted unit is the one currently selected as stock unit, clear the stock unit selection
      if (
        ingredient.data.stock_unit_id ===
        ingredient.data.units[index]?.alt_unit_id
      ) {
        draft.data.stock_unit_id = "";
      }
    });
  };
  const onDeleteTagSelect = (data) => {
    setIngredient((draft) => {
      draft.data.tags = ingredient.data.tags.filter((value) => {
        return value !== data;
      });
    });
  };
  const getAllIngredientList = async ({ search = null, page }) => {
    let params = {};
    let pagination = {
      pageIndex: page,
      pageSize: 2,
    };
    if (search) {
      params = { ...params, search };
    }
    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getUnitList({ query });
      if (res) {
        const options = res?.units?.map((data) => {
          return {
            id: data?.id,
            name: data?.name,
          };
        });
        return {
          options: options,
          hasMore: res?.pagination?.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };
  const onClearFilter = () => {
    clearAllFilters({ tableName: moduleName });
  };

  const disableclearButton =
    (tableData.ingredients.data.selectedStatus == null ||
      tableData.ingredients.data.selectedStatus === "") &&
    !tableData.ingredients.data.types.length &&
    !tableData.ingredients.data.tagslist.length &&
    !tableData.ingredients.data.supplierlist.length
      ? true
      : false;

  const typeValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({ tableName: moduleName, key: "types", value: item });
  };
  const tagValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({ tableName: moduleName, key: "tagslist", value: item });
  };
  const supplierValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({ tableName: moduleName, key: "supplierlist", value: item });
  };
  const handleDropzonePdf = (event) => {
    const doc_urls = event.target.files[0];
    let pdfFormdata = new FormData();
    pdfFormdata.append("type", "doc");
    pdfFormdata.append("media", doc_urls);
    createPdfUrl.mutate(pdfFormdata);
  };

  useEffect(() => {
    if (createPdfUrl?.data) {
      const pdfUrl = createPdfUrl?.data.url;
      if (pdfUrl) {
        const hasAdded = state.data.pdf.some((obj) =>
          Object.values(obj).includes(pdfUrl.url),
        );
        if (!hasAdded) {
          setState((draft) => {
            draft.data.pdf.push(createPdfUrl?.data);
          });
        } else {
          console.log(`${pdfUrl} already exists in the array of objects.`);
        }
      }
    }
  }, [createPdfUrl?.data]);
  const handleDeletePdf = (index) => {
    setState((draft) => {
      draft.data.pdf = [...draft.data.pdf.filter((data, i) => i !== index)];
    });
  };
  const onDeleteSupplierSelect = (data) => {
    setIngredient((draft) => {
      draft.data.suppliers = ingredient.data.suppliers.filter((value) => {
        return value !== data;
      });
    });
  };
  const handleDialogClose = () => {
    setIngredient((draft) => {
      draft.isErrorMessage = false;
    });
  };

  const handleSyncModal = (isOpen) => {
    setIngredient((draft) => {
      draft.isOpenSyncModal = isOpen;
      draft.data.sync_type = "scheduled";
    });
  };

  const handleScheduledChange = (event) => {
    const { value, name } = event.target;
    setIngredient((draft) => {
      draft.data[name] = value;
    });
  };

  return {
    listQuery,
    createIngredient,
    onChange,
    onSubmit,
    updateIngredient,
    handleClickOpen,
    handleClose,
    ingredient,
    ingredientDetails,
    searchHandler,
    validator,
    clearSearch,
    handleDeleteModal,
    deleteIngredient,
    onSearchChange,
    pagination,
    setPagination,
    deleteInfo,
    onDelete,
    resetDeleteInfo,
    confirmDelete,
    optionsTaxGroup,
    globalData,
    handleProductInputChange,
    onTableChange,
    handleAddUnits,
    handleDelete,
    onTablehandleChange,
    tagsListQuery,
    onDeleteTagSelect,
    sorting,
    setSorting,
    getAllIngredientList,
    selectStatus,
    disableclearButton,
    onClearFilter,
    typeValuesHandler,
    optionsTagsList,
    tagValuesHandler,
    optionsTypes,
    onChangeTax,
    handleDropzonePdf,
    state,
    createPdfUrl,
    handleDeletePdf,
    unitValidator,
    isCreateVisible,
    isEditable,
    isDeletable,
    tableData,
    moduleName,
    search,
    handleSupplierInputChange,
    optionsTags,
    optionsSupplier,
    optionsSupplierList,
    supplierValuesHandler,
    onDeleteSupplierSelect,
    handleDialogClose,
    onVariantChange,
    onVariantAddRemove,
    handleSyncModal,
    handleScheduledChange,
    handleOnSubmit,
  };
};
